// src/communaute/AddPostModal.jsx
//
// Nouvelle annonce : titre*, description*, 5 images max (4 Mo chacune),
// liens YouTube / Facebook / TikTok / Instagram.

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Facebook, ImagePlus, Instagram, Link2, Music2, X, Youtube } from 'lucide-react';

import { creerPost } from './api';
import { Modal, Spinner, btnPrimaire, champ, toast } from './ui';

const MAX_IMAGES = 5;
const MAX_OCTETS = 4 * 1024 * 1024; // le backend refuse au-delà (max:4096)

const VIDE = { titre: '', contenu: '', lien_youtube: '', lien_facebook: '', lien_tiktok: '', lien_instagram: '' };

export default function AddPostModal({ ouvert, onFermer, onCree }) {
    const [f, setF] = useState(VIDE);
    const [images, setImages] = useState([]); // { file, url }
    const [liens, setLiens] = useState(false);
    const [envoi, setEnvoi] = useState(false);
    const inputFichier = useRef(null);

    // Libère les aperçus à la fermeture
    useEffect(() => {
        if (ouvert) return;
        images.forEach((i) => URL.revokeObjectURL(i.url));
        setImages([]);
        setF(VIDE);
        setLiens(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ouvert]);

    const maj = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const ajouterImages = (e) => {
        const fichiers = Array.from(e.target.files || []);
        e.target.value = '';
        const place = MAX_IMAGES - images.length;
        if (place <= 0) {
            toast(`${MAX_IMAGES} images maximum`);
            return;
        }
        const valides = [];
        fichiers.slice(0, place).forEach((file) => {
            if (!file.type.startsWith('image/')) return;
            if (file.size > MAX_OCTETS) {
                toast(`« ${file.name} » dépasse 4 Mo`);
                return;
            }
            valides.push({ file, url: URL.createObjectURL(file) });
        });
        if (fichiers.length > place) toast(`${MAX_IMAGES} images maximum`);
        setImages((l) => [...l, ...valides]);
    };

    const retirerImage = (i) =>
        setImages((l) => {
            URL.revokeObjectURL(l[i].url);
            return l.filter((_, j) => j !== i);
        });

    const publier = async () => {
        if (!f.titre.trim()) return toast('Le titre est obligatoire');
        const contenu = f.contenu.replace(/\n{3,}/g, '\n\n').trim();
        if (!contenu) return toast('La description est obligatoire');

        setEnvoi(true);
        const r = await creerPost({ ...f, contenu }, images.map((i) => i.file));
        setEnvoi(false);

        if (r.erreur) {
            toast(r.erreur);
            return;
        }
        toast('Publication créée avec succès', true);
        onCree?.(r.post);
        onFermer();
    };

    const lienChamp = (cle, icone, placeholder) => {
        const Icone = icone;
        return (
            <div className="relative">
                <Icone size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="url" value={f[cle]} onChange={maj(cle)} placeholder={placeholder} className={`${champ} pl-11`} />
            </div>
        );
    };

    return (
        <Modal
            ouvert={ouvert}
            onFermer={envoi ? undefined : onFermer}
            titre="Nouvelle annonce"
            taille="lg"
            pied={
                <div className="flex justify-end">
                    <button onClick={publier} disabled={envoi} className={`${btnPrimaire} min-w-[140px]`}>
                        {envoi ? <Spinner taille={18} className="!text-white" /> : 'Publier'}
                    </button>
                </div>
            }
        >
            <div className="space-y-6 p-5">
                {/* Titre */}
                <div>
                    <label className="mb-2 block text-[15px] font-bold text-gray-900">
                        Titre <span className="text-[#9B59B6]">*</span>
                    </label>
                    <input value={f.titre} onChange={maj('titre')} placeholder="Titre de votre annonce" maxLength={255} className={champ} />
                </div>

                {/* Description */}
                <div>
                    <label className="mb-2 block text-[15px] font-bold text-gray-900">
                        Description <span className="text-[#9B59B6]">*</span>
                    </label>
                    <textarea
                        value={f.contenu}
                        onChange={maj('contenu')}
                        rows={7}
                        placeholder="Écrivez votre description ici…"
                        className={`${champ} resize-y leading-relaxed`}
                    />
                </div>

                {/* Images */}
                <div>
                    <label className="mb-2 block text-[15px] font-bold text-gray-900">
                        Images{' '}
                        <span className="text-[13px] font-normal text-gray-400">
                            {images.length}/{MAX_IMAGES} — optionnel, 4 Mo max
                        </span>
                    </label>
                    <div className="flex flex-wrap gap-3">
                        {images.length < MAX_IMAGES && (
                            <button
                                onClick={() => inputFichier.current?.click()}
                                className="flex h-24 w-24 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-100 text-gray-500 hover:border-[#7C3AED] hover:text-gray-900"
                            >
                                <ImagePlus size={28} />
                            </button>
                        )}
                        {images.map((img, i) => (
                            <div key={img.url} className="relative h-24 w-24">
                                <img src={img.url} alt="" className="h-full w-full rounded-xl object-cover" />
                                <button
                                    onClick={() => retirerImage(i)}
                                    className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-black"
                                >
                                    <X size={13} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <input ref={inputFichier} type="file" accept="image/*" multiple hidden onChange={ajouterImages} />
                </div>

                {/* Liens */}
                <div>
                    <label className="mb-2 block text-[15px] font-bold text-gray-900">
                        Liens externes <span className="text-[13px] font-normal text-gray-400">optionnel</span>
                    </label>
                    <button
                        onClick={() => setLiens((l) => !l)}
                        className="flex w-full items-center gap-3 rounded-xl bg-gray-100 px-4 py-3 text-left text-sm text-gray-600 hover:bg-gray-100"
                    >
                        <Link2 size={18} className="text-gray-500" />
                        <span className="flex-1">YouTube · Facebook · TikTok · Instagram</span>
                        {liens ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                    {liens && (
                        <div className="mt-3 space-y-3">
                            {lienChamp('lien_youtube', Youtube, 'https://youtube.com/…')}
                            {lienChamp('lien_facebook', Facebook, 'https://facebook.com/…')}
                            {lienChamp('lien_tiktok', Music2, 'https://tiktok.com/…')}
                            {lienChamp('lien_instagram', Instagram, 'https://instagram.com/…')}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}